import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IPermission,
  useCheckPermissionLatestPcf,
  useReactivatePermission,
} from '../../../../../../../../hooks/api/permissions';
import useDialogContext from '../../../../../../../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../../../../../../../hooks/notification/useNotificationContext';
import {
  CheckPermissionLatestPcfErrorCode,
  CheckPermissionLatestPcfErrorCodeTranslationKeyMap,
} from '../../../../../../../../shared/enums/permission-error-code';
import { ToastSeverity } from '../../../../../../../../shared/enums/toast-severity';
import { getPermissionErrorMessage } from '../../../../../../../../shared/helpers/get-permission-error-message';

type ReactivatePermissionButtonProps = {
  permission: IPermission;
};

const ReactivatePermissionButton = ({ permission }: ReactivatePermissionButtonProps) => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const { notify } = useNotificationContext();

  const queryClient = useQueryClient();

  const {
    data,
    refetch: checkPermissionLatestPcf,
    isFetching: isCheckingPermissionLatestPcf,
    isError,
    error,
  } = useCheckPermissionLatestPcf(permission.id, {
    enabled: false,
    retry: false,
    cacheTime: 0, // Disable caching so that error state will be reset on unmount or page change
  });

  const onReactivateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['permissions', permission.productId.toString()] });
  };

  const onReactivateError = (error: Error) => {
    const errorMessage = getPermissionErrorMessage('reactivate', error, t);

    notify({
      severity: ToastSeverity.ERROR,
      detail: errorMessage,
      life: 3000,
    });
  };

  const onReactivateSettled = () => {
    // Reset the query for checking the latest PCF after reactivation, so that if reactivation fails and user clicks on the button again,
    // it will run the query to check the latest pcf again. Otherwise it will use the cached data and the query will never run.
    queryClient.resetQueries({
      queryKey: ['check-permission-latest-pcf', permission.id],
    });
  };

  const { mutate: reactivatePermission, isLoading: isReactivatingPermission } =
    useReactivatePermission({
      onSuccess: onReactivateSuccess,
      onError: onReactivateError,
      onSettled: onReactivateSettled,
    });

  useEffect(() => {
    if (data) {
      if (data.hasLatestPcf) {
        // If there is a latest pcf available, open the dialog
        return openDialog('reactivate-permission-dialog', {
          permission,
        });
      } else {
        // Otherwise, reactivate the permission directly
        return reactivatePermission({
          permissionId: permission.id,
          shareLatestPCf: false,
        });
      }
    }
  }, [data, isCheckingPermissionLatestPcf]);

  // Listen for error changes
  useEffect(() => {
    if (isError && error) {
      const errorCode = (error as Error).message;

      let errorMessage = '';

      if (
        Object.values(CheckPermissionLatestPcfErrorCode).includes(
          errorCode as CheckPermissionLatestPcfErrorCode,
        )
      ) {
        // It's a known error code, handle it accordingly
        errorMessage = t(
          `reactivatePermissionDialog.checkLatestPcfErrors.${
            CheckPermissionLatestPcfErrorCodeTranslationKeyMap[
              errorCode as CheckPermissionLatestPcfErrorCode
            ]
          }`,
        );
      } else {
        // It's an unknown error code, show generic error message
        errorMessage = t('reactivatePermissionDialog.checkLatestPcfErrors.failedToCheckLatestPcf');
      }

      notify({
        severity: ToastSeverity.ERROR,
        detail: errorMessage,
        life: 3000,
      });
    }
  }, [error, isError]);

  return (
    <Button
      label={t('productDetailsPage.tabs.permissionsTab.buttons.reactivate')}
      className='px-2 underline cursor-pointer text-primary font-medium'
      text
      onClick={() => {
        checkPermissionLatestPcf();
      }}
      disabled={isCheckingPermissionLatestPcf || isReactivatingPermission}
    />
  );
};

export default ReactivatePermissionButton;
